import { useState } from 'react';
import {
  useCreateKnowledgeBaseCollectorMutation,
  useLazyGetKnowledgeBaseCollectorZendeskConsentLinkQuery,
  useUpdateKnowledgeBaseCollectorMutation,
} from 'src/services/knowledgeBaseService';
import { useLoader } from 'src/provider/LoaderProvider';
import { useZendeskAuthorizationPolling } from './useZendeskAuthorizationPolling';
import type { KnowledgeBaseCollector } from 'src/services/knowledgeBaseService';

/**
 * "Connect with Zendesk" for the knowledge base's Zendesk collector forms: the collector created
 * to start Zendesk OAuth (before Integrate is pressed) and whether its authorization is being
 * checked. Integrate then updates `connectedCollector` instead of creating another.
 */
export const useZendeskCollectorConnect = (kbId: number | string) => {
  const loader = useLoader();
  // Separate instance so creating the collector here doesn't trigger the form's Integrate
  // success flow.
  const [createConnectCollector] = useCreateKnowledgeBaseCollectorMutation();
  const [updateCollector] = useUpdateKnowledgeBaseCollectorMutation();
  const [getZendeskConsentLink] = useLazyGetKnowledgeBaseCollectorZendeskConsentLinkQuery();
  const [connectedCollector, setConnectedCollector] = useState<KnowledgeBaseCollector | null>(null);
  const { isCheckingAuthStatus, pollZendeskAuthorization } = useZendeskAuthorizationPolling(kbId);

  // Creates the collector on first use (or saves `body` to it again), then opens Zendesk's
  // consent page and polls until the account is authorized.
  const connectToZendesk = async (body: Record<string, unknown>) => {
    let collector = connectedCollector;

    if (!collector) {
      loader.show();
      // Runs after authorization, when Integrate is pressed.
      const { data: createdCollector, error } = await createConnectCollector({
        id: kbId,
        body: { ...body, run_immediately: false },
      });
      loader.hide();

      if (error) return;
      collector = createdCollector;
      setConnectedCollector(createdCollector);
    } else {
      // Zendesk authorizes the collector as stored, so the current values (e.g. an edited
      // subdomain) are saved before requesting consent again.
      loader.show();
      const { error } = await updateCollector({
        knowledgeBaseId: kbId,
        collectorId: collector.id,
        body: { ...body, name: collector.name, run_immediately: false },
      });
      loader.hide();

      if (error) return;
    }

    const { data: consentLink } = await getZendeskConsentLink({
      knowledgeBaseId: kbId,
      collectorId: collector.id,
    });

    if (consentLink?.authorize_url) {
      window.open(consentLink.authorize_url);
      pollZendeskAuthorization(collector.id, (authorizedCollector) =>
        setConnectedCollector({ ...collector, ...authorizedCollector }),
      );
    }
  };

  return { connectedCollector, isCheckingAuthStatus, connectToZendesk };
};
